import { Box, Tooltip } from '@chakra-ui/react'
import type { ReactNode } from 'react'

type Props = {
  /**
   * Why the wrapped control cannot be used right now. When absent the control
   * is rendered bare — there is nothing to explain.
   */
  reason?: string
  children: ReactNode
}

/**
 * Wraps a disabled control so the reason for it is readable on hover.
 *
 * The convention it encodes: a disabled action stays on screen rather than
 * disappearing, because "this button is not available and here is why" is
 * information, and silently removing it is not.
 *
 * The `Box` is load-bearing, not layout. A disabled button fires no pointer
 * events, so the tooltip trigger has to be an enabled element wrapping it.
 *
 * `LicenseDetailPage` carries its own copy of this; converging them has no
 * ticket yet.
 */
export function DisabledReasonTooltip({ reason, children }: Readonly<Props>) {
  if (!reason) {
    return <>{children}</>
  }

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <Box>{children}</Box>
      </Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content>
          <Tooltip.Arrow />
          {reason}
        </Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  )
}
